import { projectImages, pictureBlob } from './imageTransfer'
import * as Y from 'yjs'
import { sharedProject, fromBase64 } from './sharedProject.mjs'
import { readRecovery, writeRecovery, removeRecovery, flushRecovery, type LiveRecovery } from './recoveryStore'
import { useLiveRoom } from './LiveRoom'
import { LiveDocument } from './LiveDocument'
import { roomFetch } from './roomApi'
import { applyPatch, createPatch, type PatchOperation } from './projectPatch.mjs'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  readBrowserValue,
  writeBrowserValue,
  removeBrowserValue,
} from './browserStorage'
import { normalizeProject, storyWordCount } from './model'
import type {
  ProjectEnvelope,
  ProjectListResponse,
  ProjectSummary,
  StoryProject,
  SyncState,
  DraftRevision,
} from './types'

const SAVE_DELAY = 700
type SaveResponse = Omit<ProjectEnvelope, 'project'> & { project?: StoryProject; corrections?: PatchOperation[] }
const UNSAVED_RECOVERY_ERROR =
  'This draft has not saved and browser recovery is unavailable. Keep this project open, download a backup, and retry saving before switching projects or signing out.'

class UnauthorizedError extends Error {}
class MissingProjectError extends Error {}

type CachedProject = {
  live?: string | LiveRecovery
  project: StoryProject
  revision: number
  dirty: boolean
  createdAt: string
  updatedAt: string
  cachedAt: string
}
type CachedIndex = {
  projects: ProjectSummary[]
  cachedAt: string
}

function accountCache(accountId: string) {
  const LEGACY_CACHE_KEY = 'bramble:account:' + accountId + ':legacy'
  const CACHE_INDEX_KEY = 'bramble:account:' + accountId + ':index'
  const CACHE_PROJECT_PREFIX = 'bramble:account:' + accountId + ':project:'
  const fetch = (url: string, options?: RequestInit) =>
    roomFetch(accountId, url, options)
  function projectCacheKey(projectId: string) {
    return CACHE_PROJECT_PREFIX + encodeURIComponent(projectId)
  }

  function sortProjects(projects: ProjectSummary[]) {
    return [...projects].sort((left, right) =>
      right.updatedAt.localeCompare(left.updatedAt),
    )
  }

  function summaryFromProject(
    project: StoryProject,
    revision: number,
    createdAt: string,
    updatedAt: string,
  ): ProjectSummary {
    return {
      id: project.id,
      title: project.title,
      revision,
      createdAt,
      updatedAt,
      sceneCount: project.nodes.length,
      wordCount: storyWordCount(project),
    }
  }

  function summaryFromEnvelope(envelope: ProjectEnvelope) {
    return summaryFromProject(
      envelope.project,
      envelope.revision,
      envelope.createdAt,
      envelope.updatedAt,
    )
  }

  function readIndex(): ProjectSummary[] {
    try {
      const cached = JSON.parse(
        readBrowserValue(CACHE_INDEX_KEY) ?? 'null',
      ) as CachedIndex | null
      return Array.isArray(cached?.projects)
        ? sortProjects(cached.projects)
        : []
    } catch {
      return []
    }
  }

  function writeIndex(projects: ProjectSummary[]) {
    try {
      writeBrowserValue(
        CACHE_INDEX_KEY,
        JSON.stringify({
          projects,
          cachedAt: new Date().toISOString(),
        } satisfies CachedIndex),
      )
    } catch {
      // The server remains the primary store.
    }
  }

  function parseCachedProject(value: unknown): CachedProject | null {
    try {
      const cached = (typeof value === 'string' ? JSON.parse(value) : value) as Partial<CachedProject> | null
      if (!cached) return null
      let source = cached.project
      if (cached.live) {
        const doc = new Y.Doc()
        try {
          if (typeof cached.live === 'string') Y.applyUpdate(doc, fromBase64(cached.live))
          else { Y.applyUpdate(doc, cached.live.snapshot); for (const update of cached.live.updates) Y.applyUpdate(doc, update) }
          source = sharedProject(doc)
        } finally { doc.destroy() }
      }
      if (!source) return null
      const project = normalizeProject(source)
      const now = new Date().toISOString()
      return {
        project,
        revision: Number.isInteger(cached.revision)
          ? Number(cached.revision)
          : 0,
        dirty: cached.dirty === true,
        createdAt:
          typeof cached.createdAt === 'string' ? cached.createdAt : now,
        updatedAt:
          typeof cached.updatedAt === 'string'
            ? cached.updatedAt
            : typeof cached.cachedAt === 'string'
              ? cached.cachedAt
              : now,
        ...(cached.live ? { live: cached.live } : {}),
        cachedAt: typeof cached.cachedAt === 'string' ? cached.cachedAt : now,
      }
    } catch {
      return null
    }
  }

  async function readCache(projectId: string): Promise<CachedProject | null> {
    const durable = parseCachedProject(await readRecovery(projectCacheKey(projectId)))
    const current = parseCachedProject(readBrowserValue(projectCacheKey(projectId)))
    if (durable && !(current?.dirty && current.cachedAt > durable.cachedAt)) return durable
    if (current) return current

    const legacy = parseCachedProject(readBrowserValue(LEGACY_CACHE_KEY))
    if (!legacy || legacy.project.id !== projectId) return null
    storeCache(
      legacy.project,
      legacy.revision,
      legacy.dirty,
      legacy.createdAt,
      legacy.updatedAt,
    )
    return legacy
  }

  function readLegacyCache() {
    return parseCachedProject(readBrowserValue(LEGACY_CACHE_KEY))
  }

  function storeCache(
    project: StoryProject,
    revision: number,
    dirty: boolean,
    createdAt: string,
    updatedAt: string,
    live?: string | LiveRecovery,
  ) {
    const key = projectCacheKey(project.id)
    const legacy = readBrowserValue(key)
    return writeRecovery(key, {
      ...(live ? { live } : { project }), revision, dirty, createdAt, updatedAt,
      cachedAt: new Date().toISOString(),
    }).then(ok => {
      if (ok && legacy !== null && readBrowserValue(key) === legacy) removeBrowserValue(key)
      return ok
    })
  }

  async function responseJson<T>(
    response: Response,
  ): Promise<T & { error?: string }> {
    try {
      return (await response.json()) as T & { error?: string }
    } catch {
      return {} as T & { error?: string }
    }
  }

  async function getServerProjects() {
    const response = await fetch('/api/projects', {
      cache: 'no-store',
      credentials: 'same-origin',
    })
    if (response.status === 401) throw new UnauthorizedError('Session expired.')
    const value = await responseJson<ProjectListResponse>(response)
    if (!response.ok || !Array.isArray(value.projects)) {
      throw new Error(
        value.error || 'The Bramble project library is unavailable.',
      )
    }
    return sortProjects(value.projects)
  }

  async function getServerProject(projectId: string) {
    const response = await fetch(
      '/api/projects/' + encodeURIComponent(projectId) + '?live=1',
      {
        cache: 'no-store',
        credentials: 'same-origin',
      },
    )
    if (response.status === 401) throw new UnauthorizedError('Session expired.')
    const value = await responseJson<ProjectEnvelope>(response)
    if (response.status === 404) {
      throw new MissingProjectError(
        value.error || 'That Bramble project is no longer on the server.',
      )
    }
    if (!response.ok || !value.project) {
      throw new Error(value.error || 'That Bramble project is unavailable.')
    }
    return { ...value, project: normalizeProject(value.project), originalProject: value.project }
  }

  return {
    projectCacheKey,
    sortProjects,
    summaryFromProject,
    summaryFromEnvelope,
    readIndex,
    writeIndex,
    readCache,
    readLegacyCache,
    storeCache,
    responseJson,
    getServerProjects,
    getServerProject,
    fetch,
  }
}

export function useProjectPersistence(
  accountId: string,
  onUnauthorized?: () => void,
) {
  const {
    projectCacheKey,
    sortProjects,
    summaryFromProject,
    summaryFromEnvelope,
    readIndex,
    writeIndex,
    readCache,
    readLegacyCache,
    storeCache,
    responseJson,
    getServerProjects,
    getServerProject,
    fetch,
  } = useMemo(() => accountCache(accountId), [accountId])
  const [projects, setProjectsState] = useState<ProjectSummary[] | null>(null)
  const [project, setProjectState] = useState<StoryProject | null>(null)
  const [activeProjectId, setActiveProjectIdState] = useState<string | null>(
    null,
  )
  const [syncState, setSyncState] = useState<SyncState>('loading')
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null)
  const [libraryError, setLibraryError] = useState<string | null>(null)
  const [syncError, setSyncError] = useState<string | null>(null)
  const [cacheAvailable, setCacheAvailable] = useState(true)
  const cacheAvailableRef = useRef(true)
  const [canRecoverProject, setCanRecoverProject] = useState(false)
  const canRecoverProjectRef = useRef(canRecoverProject)
  canRecoverProjectRef.current = canRecoverProject

  const room = useLiveRoom()
  const liveRef = room.document
  const [liveHistory, setLiveHistory] = useState({ undo: false, redo: false })
  const projectsRef = useRef<ProjectSummary[]>([])
  const projectRef = useRef<StoryProject | null>(null)
  const activeProjectIdRef = useRef<string | null>(null)
  const openGenerationRef = useRef(0)
  const revisionRef = useRef(0)
  const confirmedRef = useRef<ProjectEnvelope | null>(null)
  const createdAtRef = useRef('')
  const updatedAtRef = useRef('')
  const dirtyRef = useRef(false)
  const generationRef = useRef(0)
  const saveTimerRef = useRef<number | null>(null)
  const savePromiseRef = useRef<Promise<boolean> | null>(null)
  const serverConflictRef = useRef<(ProjectEnvelope & { originalProject?: StoryProject }) | null>(null)
  const onUnauthorizedRef = useRef(onUnauthorized)
  onUnauthorizedRef.current = onUnauthorized

  const setProjectList = useCallback((next: ProjectSummary[]) => {
    const sorted = sortProjects(next)
    projectsRef.current = sorted
    setProjectsState(sorted)
    writeIndex(sorted)
  }, [])

  const replaceSummary = useCallback(
    (summary: ProjectSummary, confirmedOnServer = false) => {
      const current = projectsRef.current.find(
        (candidate) => candidate.id === summary.id,
      )
      const nextSummary =
        current?.recoveryOnly && !confirmedOnServer
          ? { ...summary, recoveryOnly: true }
          : summary
      const next = sortProjects([
        ...projectsRef.current.filter(
          (candidate) => candidate.id !== summary.id,
        ),
        nextSummary,
      ])
      setProjectList(next)
    },
    [setProjectList],
  )

  const setCurrentProject = useCallback((next: StoryProject | null) => {
    projectRef.current = next
    setProjectState(next)
  }, [])

  const setActiveProjectId = useCallback((next: string | null) => {
    activeProjectIdRef.current = next
    setActiveProjectIdState(next)
  }, [])

  const pendingRecoveryRef = useRef<Promise<boolean>>(Promise.resolve(true))
  const recoveryWriteRef = useRef(0)
  const writeCache = useCallback((...args: Parameters<typeof storeCache>) => {
    const ticket = ++recoveryWriteRef.current
    const pending = storeCache(args[0], args[1], args[2], args[3], args[4], liveRef.current?.projectId === args[0].id ? liveRef.current.recovery : undefined)
    pendingRecoveryRef.current = pending
    void pending.then(available => {
      if (ticket === recoveryWriteRef.current && args[0].id === activeProjectIdRef.current) {
        cacheAvailableRef.current = available
        setCacheAvailable(available)
      }
    })
    return pending
  }, [])

  useEffect(() => {
    const flush = () => { if (projectRef.current) void flushRecovery(projectCacheKey(projectRef.current.id)) }
    const hidden = () => { if (document.visibilityState === 'hidden') flush() }
    window.addEventListener('pagehide', flush)
    document.addEventListener('visibilitychange', hidden)
    return () => { flush(); window.removeEventListener('pagehide', flush); document.removeEventListener('visibilitychange', hidden) }
  }, [])

  const handleUnauthorized = useCallback(async () => {
    if (projectRef.current) await flushRecovery(projectCacheKey(projectRef.current.id))
    await pendingRecoveryRef.current
    if (dirtyRef.current && !cacheAvailableRef.current) {
      const message =
        'Your session expired and browser recovery is unavailable. Keep this tab open and download a backup. Sign in in another tab, then retry saving.'
      setSyncError(message)
      setLibraryError(message)
      setSyncState('error')
      return
    }
    onUnauthorizedRef.current?.()
  }, [])

  const attachDocument = (server: ProjectEnvelope, cached?: CachedProject | null) => {
    liveRef.current?.destroy(); liveRef.current = null
    if (!server.live || (cached?.dirty && !cached.live && cached.revision !== server.revision)) return null
    const live = new LiveDocument(server.project.id, server.live, room.send, room.listen, cached?.live)
    liveRef.current = live
    live.onUpdate = (next, message, local) => {
      if (liveRef.current !== live || activeProjectIdRef.current !== next.id) return
      if (local) { generationRef.current++; dirtyRef.current = true }
      if (message?.revision && message.revision >= revisionRef.current) {
        if (canRecoverProjectRef.current) { setCanRecoverProject(false); setSyncError(null); saveTimerRef.current = window.setTimeout(() => void performSave(), SAVE_DELAY) }
        revisionRef.current = message.revision
        updatedAtRef.current = message.updatedAt || updatedAtRef.current
        setLastSavedAt(updatedAtRef.current)
      }
      if (message?.ack === live.generation) { dirtyRef.current = false; setSyncError(null); setCanRecoverProject(false) }
      const changedProject = next !== projectRef.current
      if (changedProject) setCurrentProject(next)
      writeCache(next, revisionRef.current, dirtyRef.current, createdAtRef.current, updatedAtRef.current)
      replaceSummary(summaryFromProject(next, revisionRef.current, createdAtRef.current, updatedAtRef.current), true)
      setSyncState(dirtyRef.current ? navigator.onLine ? 'saving' : 'offline' : 'saved')
      setLiveHistory(previous => { const undo = live.undo.canUndo(), redo = live.undo.canRedo(); return previous.undo === undo && previous.redo === redo ? previous : { undo, redo } })
      if (local) {
        window.clearTimeout(saveTimerRef.current ?? undefined)
        saveTimerRef.current = window.setTimeout(() => void performSave(), SAVE_DELAY)
      }
      if (message?.actor && message.actor.id !== accountId) window.dispatchEvent(new CustomEvent('bramble:writing-changed', { detail: message }))
    }
    live.onError = message => {
      setSyncError(message); setSyncState('error')
      if (message.includes('Trash') || message.startsWith('Concurrent structural')) {
        setCanRecoverProject(true)
        dirtyRef.current = true
        writeCache(live.project, revisionRef.current, true, createdAtRef.current, updatedAtRef.current)
      }
    }
    return live
  }

  const performSave = useCallback(
    async (snapshot?: StoryProject, force = false): Promise<boolean> => {
      if (savePromiseRef.current) return savePromiseRef.current

      const projectToSave = snapshot ?? projectRef.current
      const projectId = activeProjectIdRef.current
      if (!projectToSave || !projectId) return true

      if (!navigator.onLine && !force) {
        setSyncState('offline')
        return false
      }

      const live = liveRef.current
      if (live?.projectId === projectId && !force) {
        const payload = live.payload
        const request = (async () => {
          setSyncState('saving')
          try {
            const response = await fetch('/api/projects/' + encodeURIComponent(projectId) + '/live', {
              method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
            })
            const value = await responseJson<import('./LiveRoom').LiveMessage>(response)
            if (response.status === 401) { handleUnauthorized(); return false }
            if (!response.ok) {
              if (response.status === 404) { setCanRecoverProject(true); live.onError('This story is in Trash or was deleted. Your browser draft is safe; restore it from Trash or recover it as a new project.') }
              throw new Error(value.error || 'Live edits could not save. Your browser copy has been kept.')
            }
            if (liveRef.current === live) live.receive({ ...value, ack: payload.generation })
            return true
          } catch (error) {
            if (liveRef.current === live) {
              dirtyRef.current = true
              writeCache(live.project, revisionRef.current, true, createdAtRef.current, updatedAtRef.current)
              setSyncError(error instanceof Error ? error.message : 'The live draft could not save.')
              setSyncState(navigator.onLine ? 'error' : 'offline')
            }
            return false
          }
        })()
        savePromiseRef.current = request
        try { return await request } finally { if (savePromiseRef.current === request) savePromiseRef.current = null }
      }

      const saveGeneration = generationRef.current
      const baseRevision = revisionRef.current
      const createdAt = createdAtRef.current
      const confirmed = confirmedRef.current
      const changes = !force && confirmed?.saveProtocol === 1 && confirmed.project.id === projectId && confirmed.revision === baseRevision
        ? createPatch(confirmed.project, projectToSave) : null
      const partial = changes !== null && changes.length <= 10000 && JSON.stringify(changes).length < JSON.stringify(projectToSave).length
      const request = (async () => {
        setSyncState('saving')
        try {
          const response = await fetch(
            '/api/projects/' + encodeURIComponent(projectId),
            {
              method: partial ? 'PATCH' : 'PUT',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(partial ? { changes, baseRevision } : {
                project: projectToSave,
                baseRevision,
                force,
              }),
            },
          )
          const value = await responseJson<SaveResponse>(response)
          if (response.status === 401) {
            handleUnauthorized()
            return false
          }
          if (response.status === 409 && value.project) {
            if (activeProjectIdRef.current === projectId) {
              serverConflictRef.current = {
                saveProtocol: value.saveProtocol,
                originalProject: value.project,
                revision: value.revision,
                createdAt: value.createdAt,
                updatedAt: value.updatedAt,
                project: normalizeProject(value.project),
              }
              dirtyRef.current = true
              setSyncError(null)
              setCanRecoverProject(false)
              setSyncState('conflict')
            }
            return false
          }
          if (response.status === 404) {
            if (activeProjectIdRef.current === projectId) {
              const latest = projectRef.current ?? projectToSave
              const updatedAt = new Date().toISOString()
              dirtyRef.current = true
              writeCache(
                latest,
                revisionRef.current,
                true,
                createdAt || updatedAt,
                updatedAt,
              )
              replaceSummary({
                ...summaryFromProject(
                  latest,
                  revisionRef.current,
                  createdAt || updatedAt,
                  updatedAt,
                ),
                recoveryOnly: true,
              })
              setSyncError(
                'This project was deleted from the server in another tab. Recover your open draft as a new project to continue saving.',
              )
              setCanRecoverProject(true)
              setSyncState('error')
            }
            return false
          }
          if (!response.ok || (!value.project && !(partial && value.saveProtocol === 1 && Array.isArray(value.corrections)))) {
            throw new Error(value.error || 'The project could not be saved.')
          }

          const confirmedProject = value.project ?? applyPatch(projectToSave, value.corrections!)
          const normalized = normalizeProject(confirmedProject)
          const envelope = { ...value, project: normalized }
          replaceSummary(summaryFromEnvelope(envelope), true)
          writeCache(
            normalized,
            value.revision,
            false,
            value.createdAt,
            value.updatedAt,
          )

          if (activeProjectIdRef.current !== projectId) return true

          confirmedRef.current = { ...envelope, project: confirmedProject }
          revisionRef.current = value.revision
          createdAtRef.current = value.createdAt
          updatedAtRef.current = value.updatedAt
          setLastSavedAt(value.updatedAt)
          setSyncError(null)
          setCanRecoverProject(false)

          if (generationRef.current === saveGeneration) {
            dirtyRef.current = false
            setSyncState('saved')
          } else {
            const latest = projectRef.current ?? projectToSave
            dirtyRef.current = true
            writeCache(
              latest,
              value.revision,
              true,
              value.createdAt,
              value.updatedAt,
            )
            window.clearTimeout(saveTimerRef.current ?? undefined)
            saveTimerRef.current = window.setTimeout(
              () => void performSave(),
              SAVE_DELAY,
            )
          }
          return true
        } catch (caught) {
          if (activeProjectIdRef.current === projectId) {
            dirtyRef.current = true
            const latest = projectRef.current ?? projectToSave
            const updatedAt = new Date().toISOString()
            writeCache(
              latest,
              revisionRef.current,
              true,
              createdAt || updatedAt,
              updatedAt,
            )
            replaceSummary(
              summaryFromProject(
                latest,
                revisionRef.current,
                createdAt || updatedAt,
                updatedAt,
              ),
            )
            setSyncError(
              navigator.onLine
                ? caught instanceof Error
                  ? caught.message
                  : 'The project could not be saved.'
                : null,
            )
            setCanRecoverProject(false)
            setSyncState(navigator.onLine ? 'error' : 'offline')
          }
          return false
        }
      })()

      savePromiseRef.current = request
      try {
        return await request
      } finally {
        if (savePromiseRef.current === request) savePromiseRef.current = null
      }
    },
    [handleUnauthorized, replaceSummary, writeCache],
  )

  const scheduleSave = useCallback(
    (next: StoryProject) => {
      window.clearTimeout(saveTimerRef.current ?? undefined)
      saveTimerRef.current = window.setTimeout(
        () => void performSave(next),
        SAVE_DELAY,
      )
    },
    [performSave],
  )

  const changeProject = useCallback(
    (next: StoryProject) => {
      if (liveRef.current?.projectId === next.id) { liveRef.current.change(next); return }
      // Internal updates are already typed and immutable. Normalize only at input boundaries
      // so ordinary typing and undo retain shared scene/block objects in large manuscripts.
      const now = new Date().toISOString()
      generationRef.current += 1
      dirtyRef.current = true
      updatedAtRef.current = now
      setCurrentProject(next)
      writeCache(
        next,
        revisionRef.current,
        true,
        createdAtRef.current || now,
        now,
      )
      replaceSummary(
        summaryFromProject(
          next,
          revisionRef.current,
          createdAtRef.current || now,
          now,
        ),
      )
      setSyncState(navigator.onLine ? 'saving' : 'offline')
      scheduleSave(next)
    },
    [replaceSummary, scheduleSave, setCurrentProject, writeCache],
  )

  const flush = useCallback(async () => {
    window.clearTimeout(saveTimerRef.current ?? undefined)
    saveTimerRef.current = null
    if (savePromiseRef.current) await savePromiseRef.current
    if (dirtyRef.current) {
      const saved = await performSave()
      return saved && !dirtyRef.current
    }
    return true
  }, [performSave])

  const prepareToLeave = useCallback(async () => {
    await flush()
    await pendingRecoveryRef.current
    if (dirtyRef.current && !cacheAvailableRef.current) {
      setLibraryError(UNSAVED_RECOVERY_ERROR)
      setSyncError(UNSAVED_RECOVERY_ERROR)
      return false
    }
    return true
  }, [flush])

  const pictureKey = project ? projectImages(project).map(image => image.assetId || '').join(',') : ''
  useEffect(() => {
    let cancelled = false
    const images = project ? projectImages(project).filter(image => image.assetId) : []
    const cache = async () => {
      while (!cancelled && images.length) {
        const image = images.shift()!
        await pictureBlob(image, accountId).catch(() => undefined)
      }
    }
    void cache(); void cache()
    return () => { cancelled = true }
  }, [pictureKey, accountId])

  const openProject = useCallback(
    async (projectId: string) => {
      if (activeProjectIdRef.current === projectId && projectRef.current)
        return true

      const opening = ++openGenerationRef.current
      if (!(await prepareToLeave())) throw new Error(UNSAVED_RECOVERY_ERROR)
      if (opening !== openGenerationRef.current) return false
      window.clearTimeout(saveTimerRef.current ?? undefined)
      serverConflictRef.current = null
      confirmedRef.current = null
      liveRef.current?.destroy(); liveRef.current = null
      generationRef.current += 1
      dirtyRef.current = false
      setActiveProjectId(projectId)
      setCurrentProject(null)
      setLibraryError(null)
      setSyncError(null)
      setCanRecoverProject(false)
      setSyncState('loading')

      const cached = await readCache(projectId)
      if (opening !== openGenerationRef.current) return false
      cacheAvailableRef.current = Boolean(cached)
      setCacheAvailable(Boolean(cached))
      try {
        const server = await getServerProject(projectId)
        if (opening !== openGenerationRef.current) return false
        confirmedRef.current = { ...server, project: server.originalProject }
        revisionRef.current = server.revision
        createdAtRef.current = server.createdAt
        updatedAtRef.current = server.updatedAt
        setLastSavedAt(server.updatedAt)
        replaceSummary(summaryFromEnvelope(server), true)

        const live = attachDocument(server, cached)
        if (cached?.dirty && live) {
          dirtyRef.current = true
          if (!cached.live) live.change(cached.project)
          setCurrentProject(live.project)
          setSyncState('saving')
          scheduleSave(live.project)
          live.transmit()
          return true
        }
        if (cached?.dirty) {
          setCurrentProject(cached.project)
          dirtyRef.current = true
          if (cached.revision === server.revision) {
            setSyncState('saving')
            scheduleSave(cached.project)
          } else {
            serverConflictRef.current = server
            setSyncError(null)
            setCanRecoverProject(false)
            setSyncState('conflict')
          }
          return true
        }

        setCurrentProject(server.project)
        writeCache(
          server.project,
          server.revision,
          false,
          server.createdAt,
          server.updatedAt,
        )
        setSyncError(null)
        setCanRecoverProject(false)
        setSyncState('saved')
        return true
      } catch (error) {
        if (opening !== openGenerationRef.current) return false
        if (error instanceof UnauthorizedError) {
          handleUnauthorized()
          return false
        }
        if (cached) {
          revisionRef.current = cached.revision
          createdAtRef.current = cached.createdAt
          updatedAtRef.current = cached.updatedAt
          const missingOnServer = error instanceof MissingProjectError
          dirtyRef.current = missingOnServer || cached.dirty
          const offlineLive = !missingOnServer && cached.live ? attachDocument({ ...cached, live: { update: 'AAA=', vector: typeof cached.live === 'object' ? cached.live.vector || 'AA==' : 'AA==' } }, cached) : null
          setCurrentProject(offlineLive?.project || cached.project)
          const cachedSummary = summaryFromProject(
            cached.project,
            cached.revision,
            cached.createdAt,
            cached.updatedAt,
          )
          replaceSummary(
            missingOnServer
              ? { ...cachedSummary, recoveryOnly: true }
              : cachedSummary,
          )
          if (missingOnServer) {
            writeCache(
              cached.project,
              cached.revision,
              true,
              cached.createdAt,
              cached.updatedAt,
            )
            setSyncError(
              'This browser has a complete recovery copy, but the server copy was deleted. Recover it as a new project to continue saving.',
            )
            setCanRecoverProject(true)
            setSyncState('error')
          } else {
            setSyncError(null)
            setCanRecoverProject(false)
            setSyncState('offline')
          }
          return true
        }
        setActiveProjectId(null)
        setSyncError(null)
        setCanRecoverProject(false)
        setSyncState(navigator.onLine ? 'error' : 'offline')
        setLibraryError(
          error instanceof Error
            ? error.message
            : 'That project could not be opened.',
        )
        return false
      }
    },
    [
      handleUnauthorized,
      prepareToLeave,
      replaceSummary,
      scheduleSave,
      setActiveProjectId,
      setCurrentProject,
      writeCache,
    ],
  )

  const createProject = useCallback(
    async (title: string) => {
      if (!(await prepareToLeave())) throw new Error(UNSAVED_RECOVERY_ERROR)
      setLibraryError(null)
      setSyncError(null)
      setCanRecoverProject(false)
      const response = await fetch('/api/projects', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title }),
      })
      const value = await responseJson<ProjectEnvelope>(response)
      if (response.status === 401) {
        handleUnauthorized()
        throw new UnauthorizedError('Session expired.')
      }
      if (!response.ok || !value.project) {
        const message = value.error || 'The new project could not be created.'
        setLibraryError(message)
        throw new Error(message)
      }

      const envelope = { ...value, project: normalizeProject(value.project) }
      generationRef.current += 1
      revisionRef.current = envelope.revision
      confirmedRef.current = { ...envelope, project: value.project }
      createdAtRef.current = envelope.createdAt
      updatedAtRef.current = envelope.updatedAt
      dirtyRef.current = false
      serverConflictRef.current = null
      setActiveProjectId(envelope.project.id)
      attachDocument(envelope)
      setCurrentProject(envelope.project)
      setLastSavedAt(envelope.updatedAt)
      replaceSummary(summaryFromEnvelope(envelope), true)
      writeCache(
        envelope.project,
        envelope.revision,
        false,
        envelope.createdAt,
        envelope.updatedAt,
      )
      setSyncError(null)
      setCanRecoverProject(false)
      setSyncState('saved')
      return envelope.project.id
    },
    [
      handleUnauthorized,
      prepareToLeave,
      replaceSummary,
      setActiveProjectId,
      setCurrentProject,
      writeCache,
    ],
  )

  const importProject = useCallback(
    async (importedProject: StoryProject) => {
      if (!(await prepareToLeave())) throw new Error(UNSAVED_RECOVERY_ERROR)
      setLibraryError(null)
      setSyncError(null)
      setCanRecoverProject(false)
      const projectToImport = normalizeProject(importedProject)
      const response = await fetch('/api/projects', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project: projectToImport }),
      })
      const value = await responseJson<ProjectEnvelope>(response)
      if (response.status === 401) {
        handleUnauthorized()
        throw new UnauthorizedError('Session expired.')
      }
      if (!response.ok || !value.project) {
        const message = value.error || 'The project could not be imported.'
        setLibraryError(message)
        throw new Error(message)
      }

      const envelope = { ...value, project: normalizeProject(value.project) }
      generationRef.current += 1
      revisionRef.current = envelope.revision
      confirmedRef.current = { ...envelope, project: value.project }
      createdAtRef.current = envelope.createdAt
      updatedAtRef.current = envelope.updatedAt
      dirtyRef.current = false
      serverConflictRef.current = null
      setActiveProjectId(envelope.project.id)
      attachDocument(envelope)
      setCurrentProject(envelope.project)
      setLastSavedAt(envelope.updatedAt)
      replaceSummary(summaryFromEnvelope(envelope), true)
      writeCache(
        envelope.project,
        envelope.revision,
        false,
        envelope.createdAt,
        envelope.updatedAt,
      )
      setLibraryError(null)
      setSyncError(null)
      setCanRecoverProject(false)
      setSyncState('saved')
      return envelope.project.id
    },
    [
      handleUnauthorized,
      prepareToLeave,
      replaceSummary,
      setActiveProjectId,
      setCurrentProject,
      writeCache,
    ],
  )

  const projectForExport = useCallback(
    async (projectId: string) => {
      if (activeProjectIdRef.current === projectId && projectRef.current) {
        await flush()
        if (projectRef.current) return projectRef.current
      }

      const cached = await readCache(projectId)
      if (cached?.dirty) return cached.project

      try {
        return (await getServerProject(projectId)).project
      } catch (caught) {
        if (caught instanceof UnauthorizedError) {
          handleUnauthorized()
          throw caught
        }
        if (cached) return cached.project
        if (!navigator.onLine) {
          throw new Error(
            'This project is not cached in this browser, so it cannot be downloaded while offline.',
          )
        }
        throw caught
      }
    },
    [flush, handleUnauthorized],
  )

  const deleteProject = useCallback(
    async (projectId: string) => {
      if (activeProjectIdRef.current === projectId && !await flush()) throw new Error('Save your current draft before moving this project to Trash.')
      setLibraryError(null)
      const response = await fetch(
        '/api/projects/' + encodeURIComponent(projectId),
        {
          method: 'DELETE',
          credentials: 'same-origin',
        },
      )
      const value = await responseJson<{ ok: boolean }>(response)
      if (response.status === 401) {
        handleUnauthorized()
        throw new UnauthorizedError('Session expired.')
      }
      const browserRecoveryOnly =
        response.status === 404 && Boolean(await readCache(projectId))
      if (!response.ok && !browserRecoveryOnly) {
        const message = value.error || 'The project could not be deleted.'
        setLibraryError(message)
        throw new Error(message)
      }

      setProjectList(
        projectsRef.current.filter((candidate) => candidate.id !== projectId),
      )
      if (activeProjectIdRef.current === projectId) {
        generationRef.current += 1
        dirtyRef.current = false
        serverConflictRef.current = null
        confirmedRef.current = null
        liveRef.current?.destroy(); liveRef.current = null
        setActiveProjectId(null)
        setCurrentProject(null)
        setSyncError(null)
        setCanRecoverProject(false)
        setSyncState('saved')
      }
      // Stop the active subscription before deleting its asynchronous cache,
      // so a late Trash notification cannot recreate the discarded draft.
      await removeRecovery(projectCacheKey(projectId))
      removeBrowserValue(projectCacheKey(projectId))
    },
    [
      flush,
      handleUnauthorized,
      setActiveProjectId,
      setCurrentProject,
      setProjectList,
    ],
  )

  const recoverProject = useCallback(async () => {
    window.clearTimeout(saveTimerRef.current ?? undefined)
    saveTimerRef.current = null
    if (savePromiseRef.current) await savePromiseRef.current

    const projectToRecover = projectRef.current
    const previousProjectId = activeProjectIdRef.current
    if (!projectToRecover || !previousProjectId || !canRecoverProject)
      return false

    setSyncState('saving')
    try {
      const response = await fetch('/api/projects', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project: projectToRecover }),
      })
      const value = await responseJson<ProjectEnvelope>(response)
      if (response.status === 401) {
        handleUnauthorized()
        return false
      }
      if (!response.ok || !value.project) {
        throw new Error(
          value.error || 'The browser recovery copy could not be restored.',
        )
      }

      const envelope = { ...value, project: normalizeProject(value.project) }
      generationRef.current += 1
      revisionRef.current = envelope.revision
      confirmedRef.current = { ...envelope, project: value.project }
      createdAtRef.current = envelope.createdAt
      updatedAtRef.current = envelope.updatedAt
      dirtyRef.current = false
      serverConflictRef.current = null
      setActiveProjectId(envelope.project.id)
      attachDocument(envelope)
      setCurrentProject(envelope.project)
      setLastSavedAt(envelope.updatedAt)
      setProjectList(
        sortProjects([
          ...projectsRef.current.filter(
            (candidate) =>
              candidate.id !== previousProjectId &&
              candidate.id !== envelope.project.id,
          ),
          summaryFromEnvelope(envelope),
        ]),
      )
      try {
        await removeRecovery(projectCacheKey(previousProjectId))
        removeBrowserValue(projectCacheKey(previousProjectId))
      } catch {
        // The restored server copy is already safe even if stale browser data cannot be removed.
      }
      writeCache(
        envelope.project,
        envelope.revision,
        false,
        envelope.createdAt,
        envelope.updatedAt,
      )
      setLibraryError(null)
      setSyncError(null)
      setCanRecoverProject(false)
      setSyncState('saved')
      return true
    } catch (caught) {
      dirtyRef.current = true
      const updatedAt = new Date().toISOString()
      writeCache(
        projectToRecover,
        revisionRef.current,
        true,
        createdAtRef.current || updatedAt,
        updatedAt,
      )
      setSyncError(
        caught instanceof Error
          ? caught.message
          : 'The browser recovery copy could not be restored.',
      )
      setCanRecoverProject(true)
      setSyncState(navigator.onLine ? 'error' : 'offline')
      return false
    }
  }, [
    canRecoverProject,
    handleUnauthorized,
    setActiveProjectId,
    setCurrentProject,
    setProjectList,
    writeCache,
  ])

  const useServerCopy = useCallback(() => {
    const server = serverConflictRef.current
    if (!server) return
    revisionRef.current = server.revision
    confirmedRef.current = { ...server, project: server.originalProject ?? server.project }
    createdAtRef.current = server.createdAt
    updatedAtRef.current = server.updatedAt
    generationRef.current += 1
    dirtyRef.current = false
    attachDocument(server)
    setCurrentProject(server.project)
    writeCache(
      server.project,
      server.revision,
      false,
      server.createdAt,
      server.updatedAt,
    )
    replaceSummary(summaryFromEnvelope(server), true)
    setLastSavedAt(server.updatedAt)
    serverConflictRef.current = null
    setSyncError(null)
    setCanRecoverProject(false)
    setSyncState('saved')
  }, [replaceSummary, setCurrentProject, writeCache])

  const createNamedDraft = useCallback(
    async (name: string): Promise<DraftRevision> => {
      const generation = generationRef.current
      if (!(await flush()))
        throw new Error(
          'Save or resolve the current draft before naming a saved version.',
        )
      if (generation !== generationRef.current || !activeProjectIdRef.current)
        throw new Error('The active project changed.')
      const response = await fetch(
        '/api/projects/' +
          encodeURIComponent(activeProjectIdRef.current) +
          '/revisions',
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name, baseRevision: revisionRef.current }),
        },
      )
      if (response.status === 401) {
        handleUnauthorized()
        throw new Error('Sign in again to save draft history.')
      }
      const value = await responseJson<DraftRevision>(response)
      if (!response.ok || !value.id)
        throw new Error(
          value.error ||
            'The named draft could not be saved. Your current story is still available.',
        )
      return value
    },
    [flush, handleUnauthorized],
  )

  const keepBrowserCopy = useCallback(() => {
    serverConflictRef.current = null
    void performSave(undefined, true)
  }, [performSave])

  useEffect(() => {
    let cancelled = false
    const cachedIndex = readIndex()
    const legacy = readLegacyCache()
    if (!cachedIndex.length && legacy) {
      cachedIndex.push(
        summaryFromProject(
          legacy.project,
          legacy.revision,
          legacy.createdAt,
          legacy.updatedAt,
        ),
      )
    }

    const load = async () => {
      try {
        const serverProjects = await getServerProjects()
        if (cancelled) return
        const merged = await Promise.all(serverProjects.map(async (summary) => {
          const cached = await readCache(summary.id)
          return cached?.dirty
            ? summaryFromProject(
                cached.project,
                cached.revision,
                cached.createdAt || summary.createdAt,
                cached.updatedAt,
              )
            : summary
        }))
        let recoveryOnlyCount = 0
        for (const summary of cachedIndex) {
          const cached = await readCache(summary.id)
          if (
            cached?.dirty &&
            !merged.some((candidate) => candidate.id === summary.id)
          ) {
            merged.push({ ...summary, recoveryOnly: true })
            recoveryOnlyCount += 1
          }
        }
        if (cancelled) return
        setProjectList(merged)
        if (recoveryOnlyCount > 0) {
          setLibraryError(
            `${recoveryOnlyCount} browser recovery ${recoveryOnlyCount === 1 ? 'copy is' : 'copies are'} not currently on the server. Open one to restore it or delete it to discard it.`,
          )
          if (!activeProjectIdRef.current) setSyncState('error')
        } else {
          setLibraryError(null)
          if (!activeProjectIdRef.current) setSyncState('saved')
        }
      } catch (error) {
        if (cancelled) return
        if (error instanceof UnauthorizedError) {
          handleUnauthorized()
          return
        }
        setProjectList(cachedIndex)
        setLibraryError(
          cachedIndex.length
            ? 'The server is unavailable. Cached projects can still be opened.'
            : 'The project library is unavailable while offline.',
        )
        if (!activeProjectIdRef.current) setSyncState(navigator.onLine ? 'error' : 'offline')
      }
    }
    void load()

    const handleOnline = () => {
      if (dirtyRef.current) {
        if (canRecoverProjectRef.current) setSyncState('error')
        else void performSave()
        return
      }
      void load()
    }
    const handleRemote = () => { void load() }
    window.addEventListener('bramble:remote-change', handleRemote)
    const handleOffline = () => setSyncState('offline')
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    const retryTimer = window.setInterval(() => {
      if (
        dirtyRef.current &&
        navigator.onLine &&
        !serverConflictRef.current &&
        !canRecoverProjectRef.current
      )
        void performSave()
    }, 5_000)

    return () => {
      cancelled = true
      window.clearTimeout(saveTimerRef.current ?? undefined)
      window.clearInterval(retryTimer)
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      window.removeEventListener('bramble:remote-change', handleRemote)
    }
  }, [handleUnauthorized, performSave, setProjectList])

  useEffect(() => {
    const protectUnsavedDraft = (event: BeforeUnloadEvent) => {
      if (!dirtyRef.current) return
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', protectUnsavedDraft)
    return () => window.removeEventListener('beforeunload', protectUnsavedDraft)
  }, [])

  useEffect(() => () => { liveRef.current?.destroy(); liveRef.current = null }, [])

  return {
    liveHistory,
    isLive: Boolean(liveRef.current),
    liveUndo: () => { if (!liveRef.current) return false; liveRef.current.undo.undo(); return true },
    liveRedo: () => { if (!liveRef.current) return false; liveRef.current.undo.redo(); return true },
    handleUnauthorized,
    projects,
    project,
    activeProjectId,
    syncState,
    lastSavedAt,
    libraryError,
    syncError,
    canRecoverProject,
    cacheAvailable,
    prepareToLeave,
    changeProject,
    flush,
    createNamedDraft,
    openProject,
    createProject,
    importProject,
    projectForExport,
    deleteProject,
    recoverProject,
    useServerCopy,
    keepBrowserCopy,
  }
}
